import { Preferences } from '@capacitor/preferences';
import { NativeBiometric } from '@capgo/capacitor-native-biometric';

/** The index the `@capgo` Vault kept, because it could not read a key to find out whether it existed. */
const LEGACY_KEY_INDEX = 'ninety.auth.vaultKeys';

/** The gating preference. Nothing reads it any more — the App Lock keeps its own. */
const LEGACY_GATING_PREFERENCE = 'ninety.auth.biometricsEnabled';

/**
 * Delete what the `@capgo`-backed Vault left on this device.
 *
 * Nothing is migrated, deliberately. The store changed, and reading a *gated* entry out of the old
 * one would raise the very prompt this refactor exists to remove — on a launch, before anyone has
 * asked for anything. This branch has never shipped, so no production device holds these entries at
 * all; a dev or QA device signs in once more, which the spec already lists as an accepted Forced
 * Sign-out for a session-format change.
 *
 * Only the gated write ever needed the plugin's data API, so `deleteData` is the last call this
 * module makes to it: after this, `@capgo` is reached only by the App Lock.
 *
 * The old index is the only thing consulted for what to delete, so this cannot name a key the new
 * store owns — and the two stores share a `ninety.auth.` prefix.
 *
 * Every failure is swallowed. A key that was never written has nothing to delete, and a purge that
 * could fail a launch would be a worse bug than the residue it removes.
 */
export async function purgeLegacyVault(): Promise<void> {
  for (const key of await legacyKeys()) {
    await NativeBiometric.deleteData({ key }).catch(() => undefined);
  }

  await Preferences.remove({ key: LEGACY_KEY_INDEX }).catch(() => undefined);
  await Preferences.remove({ key: LEGACY_GATING_PREFERENCE }).catch(() => undefined);
}

async function legacyKeys(): Promise<readonly string[]> {
  try {
    const { value } = await Preferences.get({ key: LEGACY_KEY_INDEX });
    if (!value) return [];
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
}
